import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

import type { User } from '@/api/auth';
import { isProduction } from '@/helpers/common';
import {
  groupMemberDepositor,
  groupMemberNoDeposits,
  groupMemberVIP,
} from '@/helpers/user';
import { createWaitForGlobal } from '@/helpers/waitForGlobal';
import {
  HOTJAR_STAG_EVENT_NAME,
  hasSucceededDepositBeforeCutoff,
  isTargetStag,
  trackHotjarEvent,
} from '@/modules/hotjarStagEvent';
import { useGetCommonStore, useLocalStorageStore } from '@/store';

declare global {
  interface Window {
    hj?: (...args: any[]) => void;
  }
}

enum HotjarMode {
  ON = 0,
  OFF = 1,
}

type HotjarMap = {
  [key in HotjarMode]: {
    identifyUser: (user: User, stag: string | null) => void;
  };
};

const waitForHotjar = createWaitForGlobal(
  () => (typeof window.hj === 'function' ? window.hj : null),
  { name: 'Hotjar' }
);

const hotjarMap: HotjarMap = {
  [HotjarMode.ON]: {
    identifyUser: (user, stag) => {
      waitForHotjar()
        .then((hj) => {
          try {
            hj('identify', user.id || null, {
              email: user.email,
              vip: groupMemberVIP(user) ? 'YES' : 'NO',
              depositor: groupMemberDepositor(user) ? 'YES' : 'NO',
              groups: user.statuses?.map(({ id }) => id).join(', ') || null,
              stag,
              // TODO: Add profile data
              // profile: profileGroup,
            });
          } catch (error) {
            Sentry.captureException(JSON.stringify(error), {
              tags: {
                category: 'hotjar',
                pathToFile: 'src/hooks/3dParty/hotjar.tsx',
              },
              level: 'warning',
            });
            console.error('Hotjar identifyUser failed');
          }
        })
        .catch(() => {});
    },
  },
  [HotjarMode.OFF]: {
    identifyUser: () => {},
  },
};

const Hotjar = isProduction()
  ? hotjarMap[HotjarMode.ON]
  : hotjarMap[HotjarMode.OFF];

export const useHotjar = () => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();

  const storageStag = useLocalStorageStore
    .getState()
    .get<string | null>('stag', null);

  useEffect(() => {
    if (!user) return;

    Hotjar.identifyUser(user, storageStag);
  }, [user, storageStag]);
};

// DEV-1555: fire `hj_event_stag_present` for stag-149232 users whose first
// deposit is after 2026-06-01, so Hotjar starts recording their sessions.
const HOTJAR_STAG_EVENT_SESSION_KEY = 'hj_stag_event_fired';

export const useHotjarStagEvent = () => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();

  useEffect(() => {
    if (!user) return;
    if (!isTargetStag()) return;
    // group 00002 = has not deposited → no FTD to check, skip the request.
    if (groupMemberNoDeposits(user)) return;
    if (sessionStorage.getItem(HOTJAR_STAG_EVENT_SESSION_KEY)) return;

    hasSucceededDepositBeforeCutoff(user)
      .then((hasDepositBeforeCutoff) => {
        if (hasDepositBeforeCutoff) return;

        trackHotjarEvent(HOTJAR_STAG_EVENT_NAME);
        sessionStorage.setItem(HOTJAR_STAG_EVENT_SESSION_KEY, '1');
      })
      .catch(() => {});
  }, [user]);
};
